import axios, { AxiosStatic } from 'axios';
import { ConfigModelType, mergeFrontendConfig } from 'model/config';
import React, { useContext } from 'react';
import { ApiClient, createApiClient } from 'utils/api/ApiClient';

export type UiContext = Readonly<{
  config: ConfigModelType['FrontendConfig'];
  apiClient: ApiClient;
}>;

// Prepares a FrontendConfig for use in a browser context
export function createFrontendConfig() {
  return mergeFrontendConfig(
    // First, apply any config overrides from BUILD TIME
    process.env.REACT_APP_CONFIG,
    // Then, apply possible config overrides from DEPLOY TIME
    (document.querySelector('script[type="application/frontend-config"]') || { textContent: '' }).textContent,
    // Finally, apply any RUNTIME overrides; these are subjected to more scrutiny than other sources
    [window.location.search, (key: any) => !!key.match(/^FEATURE_/)], // only allow changing feature flags
  );
}

export type UiContextOverrides = Partial<UiContext & { axios: AxiosStatic }>;

export function createUiContext(overrides: UiContextOverrides = {}): UiContext {
  const config = overrides.config || mergeFrontendConfig();
  const apiClient = overrides.apiClient || createApiClient(config, overrides.axios || axios);

  return {
    config,
    apiClient,
  };
}

// This React Context is INTENTIONALLY not exported from this module.
// The idea being that instead of using <UiContext> and its various companions,
// all call sites should either go through the useUiContext() hook, or the
// <UiContextProvider> component.
const UiContext = React.createContext<UiContext | null>(null);

export const UiContextProvider: React.FC<{ uiContext: UiContext }> = ({ uiContext, children }) => (
  <UiContext.Provider value={uiContext}>
    {children}
    {/* the children can be either a full <App> instance, or an individual component */}
  </UiContext.Provider>
);

export function useUiContext(): UiContext {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('This component cannot be rendered without UiContext being available');
  return ctx;
}
